import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDTypography from "../../../components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// NewProduct page components
import UserInfo from "./components/UserInfo";
import userService from "../../../services/userService";

const userSchema = yup.object().shape({
  username: yup
    .string()
    .required("El nombre de usuario es obligatorio")
    .min(3, "Mínimo 3 caracteres")
    .trim(),

  password: yup
    .string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .when("$isEdit", (isEdit, schema) => {
      return isEdit
        ? schema
          .min(8, "La nueva contraseña debe tener al menos 8 caracteres")
          .notRequired()
        : schema
          .required("La contraseña es obligatoria")
          .min(8, "La contraseña debe tener al menos 8 caracteres");
    }),

  role: yup.string().required("El rol es obligatorio"),
});

const steps = ["1. Sobre el usuario"];

function NewUser() {
  let navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    mode: "onChange",
    context: { isEdit: !!id },
    defaultValues: {
      username: "",
      password: "",
      role: "",
    },
  });

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data } = await userService.getById(id);
        const user = data;

        Object.keys(user).forEach((key) => {
          const value = user[key] || "";
          setValue(key, value);
        });
      } catch (error) {
        console.error("Error cargando usuarios:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id, setValue]);

  const handleNext = async () => {
    const fieldsToValidate =
      activeStep === 0
        ? ["username", "password", "role"]
        : [];

    const result = await trigger(fieldsToValidate);

    if (result) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!id) {
        await userService.create(data);
      } else {
        await userService.update(id, data);
      }
      navigate("/users");
    } catch (error) {
      console.error("Error guardando user:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar. Revisa la consola.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                {id ? "Editar" : "Registrar nuevo"} usuario
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                Información del usuario
              </MDTypography>
            </MDBox>

            <Card>
              <MDBox mt={-3} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>

              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {activeStep === 0 && (
                    <UserInfo control={control} errors={errors} isEdit={!!id} />
                  )}

                  <MDBox mt={4} display="flex" justifyContent="space-between">
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="light" onClick={handleBack}>
                        Atrás
                      </MDButton>
                    )}

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={isLastStep ? handleSubmit(onSubmit) : handleNext}
                      disabled={isLoading}
                    >
                      {isLoading ? "Guardando..." : isLastStep ? "Guardar" : "Siguiente"}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewUser;
